import React, { useState } from "react";
import { Input, Button, Typography, Space, Descriptions, message, Card, Select, DatePicker, Row, Col } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const commonTimezones = [
    "UTC",
    "Asia/Seoul",
    "Asia/Tokyo",
    "America/New_York",
    "America/Los_Angeles",
    "Europe/London",
    "Europe/Paris",
    "Asia/Shanghai",
    "Australia/Sydney",
    "Asia/Kolkata"
];

export default function TimestampConverter() {
    const [timestampInput, setTimestampInput] = useState("");
    const [dateInput, setDateInput] = useState(null);
    const [dateResult, setDateResult] = useState(null);
    const [unixResult, setUnixResult] = useState(null);
    const [selectedTz, setSelectedTz] = useState(dayjs.tz.guess());
    const [convertDate, setConvertDate] = useState(null);
    const [fromTz, setFromTz] = useState(dayjs.tz.guess());
    const [toTz, setToTz] = useState("UTC");
    const [convertedDate, setConvertedDate] = useState(null);

    const convertTimestampToDate = () => {
        let ts = timestampInput.trim();
        let date;
        if (/^\d+$/.test(ts)) {
            const ms = ts.length > 10 ? parseInt(ts) : parseInt(ts) * 1000;
            date = dayjs(ms);
        } else {
            message.error("Invalid timestamp input.");
            return;
        }

        if (!date.isValid()) {
            message.error("Invalid timestamp value.");
            return;
        }

        try {
            const tzFormatted = date.tz(selectedTz).format("YYYY-MM-DD HH:mm:ss");
            const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const localOffset = dayjs().tz(localZone).format("Z").replace(":", "");
            setDateResult({
                raw: date,
                iso: date.toISOString(),
                local: `${date.format("YYYY-MM-DD HH:mm:ss")}`,
                localZone: `${localZone} (${localOffset})`,
                tz: tzFormatted,
                tzLabel: `${selectedTz} (${dayjs().tz(selectedTz).format("Z").replace(":", "")})`
            });
        } catch (err) {
            message.error("Invalid time zone specified.");
        }
    };

    const convertDateToTimestamp = () => {
        try {
            const parsed = dayjs.tz(dateInput, selectedTz);
            if (!parsed.isValid()) {
                message.error("Invalid date input");
                return;
            }
            setUnixResult({
                ms: parsed.valueOf(),
                sec: Math.floor(parsed.valueOf() / 1000),
            });
        } catch {
            message.error("Invalid time zone specified.");
        }
    };

    const convertDateAcrossTimezones = () => {
        try {
            const parsed = dayjs.tz(convertDate, fromTz);
            const converted = parsed.tz(toTz).format("YYYY-MM-DD HH:mm:ss");
            setConvertedDate(converted);
        } catch {
            message.error("Invalid time zone specified.");
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => message.success("Copied!"));
    };

    const timezoneOptions = commonTimezones.map(tz => {
        const offset = dayjs().tz(tz).format("Z");
        return {
            label: `${tz} (${offset})`,
            value: tz
        };
    });

    return (
        <div style={{ padding: 24 }}>
            <Typography.Title level={3}>Timestamp Converter</Typography.Title>

            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Timestamp to Date */}
                <Card title="Unix Timestamp → Date">
                    <Row gutter={16} align="middle">
                        <Col>
                            <Input
                                placeholder="Enter timestamp (ms/s)"
                                value={timestampInput}
                                onChange={(e) => setTimestampInput(e.target.value)}
                                style={{ width: 300 }}
                            />
                        </Col>
                        <Col>
                            <Button type="primary" onClick={convertTimestampToDate}>Convert</Button>
                        </Col>
                    </Row>

                    {dateResult && (
                        <Descriptions variant="outlined" column={1} size="small" labelStyle={{ width: 140, marginTop: 12 }} style={{ marginTop: 16 }}>
                            <Descriptions.Item label="ISO">
                                <Space>
                                    {dateResult.iso}
                                    <Button size="small" onClick={() => copyToClipboard(dateResult.iso)}>Copy</Button>
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label={`Local (${dateResult.localZone})`}>
                                <Space>
                                    {dateResult.local}
                                    <Button size="small" onClick={() => copyToClipboard(dateResult.local)}>Copy</Button>
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={
                                    <Space style={{ display: 'flex', alignItems: 'center' }}>
                                        <span>Time Zone</span>
                                        <Select
                                            style={{ width: 240 }}
                                            value={selectedTz}
                                            onChange={(tz) => {
                                                try {
                                                    dayjs().tz(tz);
                                                    setSelectedTz(tz);
                                                    setDateResult(prev => ({
                                                        ...prev,
                                                        tz: prev.raw.tz(tz).format("YYYY-MM-DD HH:mm:ss"),
                                                        tzLabel: `${tz} (${dayjs().tz(tz).format("Z").replace(":", "")})`
                                                    }));
                                                } catch {
                                                    message.error("Invalid time zone specified.");
                                                }
                                            }}
                                            options={timezoneOptions}
                                            showSearch
                                            allowClear={false}
                                            dropdownRender={menu => (
                                                <div>
                                                    {menu}
                                                    <div style={{ padding: '8px', fontStyle: 'italic', color: '#999' }}>
                                                        You can also type in a timezone
                                                    </div>
                                                </div>
                                            )}
                                        />
                                    </Space>
                                }
                            >
                                <Space>
                                    {dateResult.tz}
                                    <Button size="small" onClick={() => copyToClipboard(dateResult.tz)}>Copy</Button>
                                </Space>
                            </Descriptions.Item>
                        </Descriptions>
                    )}
                </Card>

                {/* Date to Timestamp */}
                <Card title="Date → Unix Timestamp" style={{ marginTop: 24 }}>
                    <Row gutter={16} align="middle">
                        <Col>
                            <DatePicker
                                showTime
                                value={dateInput ? dayjs(dateInput) : null}
                                onChange={(val) => setDateInput(val)}
                                placeholder="Select date and time"
                            />
                        </Col>
                        <Col>
                            <Button type="primary" onClick={convertDateToTimestamp}>Convert</Button>
                        </Col>
                    </Row>

                    {unixResult && (
                        <Descriptions variant="outlined" column={1} size="small" labelStyle={{ width: 140, marginTop: 12 }} style={{ marginTop: 16 }}>
                            <Descriptions.Item label="Milliseconds">
                                <Space>
                                    {unixResult.ms}
                                    <Button size="small" onClick={() => copyToClipboard(unixResult.ms)}>Copy</Button>
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="Seconds">
                                <Space>
                                    {unixResult.sec}
                                    <Button size="small" onClick={() => copyToClipboard(unixResult.sec)}>Copy</Button>
                                </Space>
                            </Descriptions.Item>
                        </Descriptions>
                    )}
                </Card>

                {/* Date → Date with Timezone Change */}
                <Card title="Date → Date in Another Timezone" style={{ marginTop: 24 }}>
                    <Row gutter={16} align="middle">
                        <Col>
                            <DatePicker
                                showTime
                                value={convertDate ? dayjs(convertDate) : null}
                                onChange={(val) => setConvertDate(val)}
                                placeholder="Select date and time"
                            />
                        </Col>
                        <Col>
                            <Select
                                style={{ width: 180 }}
                                value={fromTz}
                                onChange={(val) => setFromTz(val)}
                                options={timezoneOptions}
                                showSearch
                            />
                        </Col>
                        <Col>
                            →
                        </Col>
                        <Col>
                            <Select
                                style={{ width: 180 }}
                                value={toTz}
                                onChange={(val) => setToTz(val)}
                                options={timezoneOptions}
                                showSearch
                            />
                        </Col>
                        <Col>
                            <Button type="primary" onClick={convertDateAcrossTimezones}>Convert</Button>
                        </Col>
                    </Row>

                    {convertedDate && (
                        <Descriptions variant="outlined" column={1} size="small" labelStyle={{ width: 140, marginTop: 12 }} style={{ marginTop: 16 }}>
                            <Descriptions.Item label={`Converted (${toTz})`}>
                                <Space>
                                    {convertedDate}
                                    <Button size="small" onClick={() => copyToClipboard(convertedDate)}>Copy</Button>
                                </Space>
                            </Descriptions.Item>
                        </Descriptions>
                    )}
                </Card>
            </Space>
        </div>
    );
}
